import React, { useState } from 'react';
// icons
import { IconContext } from 'react-icons';
import {
    Redirect,
    Route,
    BrowserRouter as Router,
    Switch,
} from 'react-router-dom';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import PrivateRoute from './components/auth/PrivateRoute';
import SideNav from './components/nav/SideNav';
// Components
import styled from 'styled-components';

// Constants
import aampeTheme from './constants/aampeTheme';
import routes from './constants/routes';

import useBreakpoint from './hooks/helper/useBreakpoint';
import { useAuthRedirect } from './hooks/useAuthRedirect';
// Views
import { useAuthSession } from './hooks/useAuthSession';

import AampeLogo from './images/logos/logoOnly.svg?react';

import envConfig from './services/env-config';

import AampeAdminMain from './views/aampeAdmin/AampeAdminMain';
import ConfigureMainLazy from './views/configure/ConfigureMain.lazy';
import DocsLogin from './views/docs/DocsLogin';
import FormulasMain from './views/formulaCatalog/FormulasMain';
import HomeDashboardV2 from './views/home/HomeDashboardV2';
import LoginMain from './views/login/LoginMain';
import FormulaSequencesMainLazy from './views/message-sequencing/FormulaSequencesMainLazy';
import MessageSettingsLazy from './views/messageSettings/MessageSettingsMain.lazy';
import MonitoringMainLazy from './views/monitoring/MonitoringMain.lazy';
import TopicManagementMainLazy from './views/monitoring/topicManagement/TopicManagementMain.lazy';
import LabelManagementMainRouteLazy from './views/personalizationManager/LabelManagementMainRoute.lazy';
import PluginAuth from './views/plugin/PluginAuth';
import SamplesLibraryMainLazy from './views/samplesLibrary/InspirationLibraryMain.lazy';
import SnippetsMainLazy from './views/snippets/SnippetsMain.lazy';
import SurfaceMainLazy from './views/surface/SurfaceMain.lazy';

export const APP_CONTAINER_ID = 'app-container';

interface AppWithSessionProps {
    getUserConfirmation: (
        message: string,
        callback: (confirmed: boolean) => void,
    ) => void;
    redirectedRoutes: React.ReactNode[];
}

export default function AppWithSession({
    getUserConfirmation,
    redirectedRoutes,
}: AppWithSessionProps) {
    const { isAuthenticated, status: authStatus } = useAuthSession();
    useAuthRedirect({ isAuthenticated, status: authStatus });
    const authorizing = authStatus === 'loading';
    const { isMobile } = useBreakpoint();
    const [showAnywayOnMobile, setShowAnywayOnMobile] = useState(false);

    if (isMobile && !showAnywayOnMobile) {
        return (
            <MobileNotSupportedScreen
                onShowAnyway={() => setShowAnywayOnMobile(true)}
            />
        );
    }

    return (
        <>
            <StyledBackdrop open={authorizing}>
                {authorizing ? <CircularProgress color="inherit" /> : null}
            </StyledBackdrop>
            <IconContext.Provider
                value={{ size: aampeTheme.size.icon.default }}
            >
                <Router
                    basename={envConfig.BASE_PATH}
                    // getUserConfirmation is fired only on pages where Prompt is used
                    getUserConfirmation={getUserConfirmation}
                >
                    <StyledAppContainer>
                        <div id="modal-root" />
                        <div id="snackbar-root" />
                        {isAuthenticated && <SideNav />}

                        <StyledContentContainer id={APP_CONTAINER_ID}>
                            <Switch>
                                {/* Redirects have grown unwieldy so they have been put in their own separate file */}
                                {redirectedRoutes}

                                <Route path={routes.auth}>
                                    <LoginMain />
                                </Route>
                                <PrivateRoute
                                    path={routes.dashboard}
                                    component={HomeDashboardV2}
                                />
                                <PrivateRoute
                                    path={routes.formulasMain}
                                    component={FormulasMain}
                                />
                                <PrivateRoute
                                    exact
                                    path={routes.inspiration}
                                    component={SamplesLibraryMainLazy}
                                />
                                <PrivateRoute
                                    path={routes.surface}
                                    component={SurfaceMainLazy}
                                />
                                <PrivateRoute
                                    path={routes.genAI}
                                    component={SnippetsMainLazy}
                                />
                                {/* collections */}
                                <PrivateRoute
                                    path={routes.monitoringMain}
                                    component={MonitoringMainLazy}
                                />
                                <PrivateRoute
                                    path={routes.labelManagement}
                                    component={LabelManagementMainRouteLazy}
                                />
                                <PrivateRoute
                                    path={routes.topicManagement}
                                    component={TopicManagementMainLazy}
                                />
                                <PrivateRoute
                                    path={routes.messageSettings}
                                    component={MessageSettingsLazy}
                                />
                                <PrivateRoute
                                    path={routes.systemSetupMain}
                                    component={ConfigureMainLazy}
                                />
                                <PrivateRoute
                                    path={routes.messageSequencing}
                                    component={FormulaSequencesMainLazy}
                                />
                                <PrivateRoute
                                    path={routes.docsLogin}
                                    component={DocsLogin}
                                />
                                <PrivateRoute
                                    path={routes.pluginAuth}
                                    component={PluginAuth}
                                />
                                <PrivateRoute
                                    path={routes.aampeAdmin}
                                    component={AampeAdminMain}
                                />

                                <Redirect to={routes.dashboard} />
                            </Switch>
                        </StyledContentContainer>
                    </StyledAppContainer>
                </Router>
            </IconContext.Provider>
        </>
    );
}

const StyledBackdrop = styled(Backdrop)({
    zIndex: 9999,
    color: '#fff',
    width: '100%',
});

const StyledAppContainer = styled.div({
    display: 'flex',
    height: '100vh',
    flexFlow: 'row nowrap',
    justifyContent: 'flex-start',
    width: '100%',
});

const StyledContentContainer = styled.div({
    display: 'flex',
    flexFlow: 'column',
    width: '100%',
    overflow: 'auto',
});

function MobileNotSupportedScreen({
    onShowAnyway,
}: {
    onShowAnyway: () => void;
}) {
    return (
        <StyledMobileScreen>
            <StyledMobileContent>
                <StyledMobileLogoWrapper>
                    <AampeLogo />
                </StyledMobileLogoWrapper>
                <StyledMobileOhNo
                    src={`${import.meta.env.BASE_URL}aampe-ascii-mobile.png`}
                    alt="Oh no!"
                />
                <StyledMobileHeading>
                    The Aampe Composer isn&apos;t available for mobile right
                    now.
                </StyledMobileHeading>
                <StyledMobileBody>
                    It seems we&apos;re in a bit of a tricky situation. The
                    Aampe Composer is packed with data and still a work in
                    progress, so it needs some more tweaking before it&apos;s
                    ready for mobile.
                </StyledMobileBody>
                <StyledMobileButtonGroup>
                    <StyledMobilePrimaryLink
                        href="https://kb.aampe.com"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Access Knowledge Base
                    </StyledMobilePrimaryLink>
                    <StyledMobileSecondaryLink
                        href="https://aampe.com"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Access Website
                    </StyledMobileSecondaryLink>
                    <StyledMobileShowAnywayButton onClick={onShowAnyway}>
                        Show me anyway
                    </StyledMobileShowAnywayButton>
                </StyledMobileButtonGroup>
            </StyledMobileContent>
        </StyledMobileScreen>
    );
}

const StyledMobileScreen = styled.div({
    display: 'flex',
    height: '100vh',
    width: '100%',
    alignItems: 'flex-start',
    justifyContent: 'center',
    backgroundColor: '#F5F3F1',
    padding: '40px 24px',
    boxSizing: 'border-box',
    overflowY: 'auto',
});

const StyledMobileContent = styled.div({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '400px',
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '32px 28px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
});

const StyledMobileLogoWrapper = styled.div({
    marginBottom: '24px',
    width: '40px',
    height: '40px',
    '& svg': {
        width: '40px',
        height: '40px',
    },
});

const StyledMobileOhNo = styled.img({
    width: '60%',
    maxWidth: '240px',
    height: 'auto',
    marginBottom: '32px',
    display: 'block',
});

const StyledMobileHeading = styled.h2({
    margin: '0 0 12px 0',
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#241F1A',
});

const StyledMobileBody = styled.p({
    margin: '0 0 32px 0',
    fontSize: '15px',
    lineHeight: 1.5,
    color: '#241F1A',
});

const StyledMobileButtonGroup = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
});

const mobileButtonBase = {
    display: 'block',
    padding: '14px 20px',
    fontSize: '15px',
    fontWeight: 600,
    textDecoration: 'none',
    borderRadius: '6px',
    textAlign: 'center' as const,
    boxSizing: 'border-box' as const,
};

const StyledMobilePrimaryLink = styled.a({
    ...mobileButtonBase,
    backgroundColor: '#E15E12',
    color: '#fff',
    border: '2px solid #E15E12',
    '&:hover': {
        backgroundColor: '#C44E0A',
        borderColor: '#C44E0A',
    },
});

const StyledMobileSecondaryLink = styled.a({
    ...mobileButtonBase,
    backgroundColor: 'transparent',
    color: '#E15E12',
    border: '2px solid #E15E12',
    '&:hover': {
        backgroundColor: '#FDE5DB',
    },
});

const StyledMobileShowAnywayButton = styled.button({
    ...mobileButtonBase,
    backgroundColor: 'transparent',
    color: '#6B6560',
    border: '2px solid #D4CEC9',
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': {
        backgroundColor: '#F0EDEA',
        borderColor: '#B8B0AA',
    },
});
